import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
} from '@angular/core';

// This directive targets the goa-pagination component to set an appropriate aria-label on its internal input and icon elements,
// ensuring that screen readers can accurately describe the pagination controls. It uses a retry mechanism to handle cases where the pagination's
// content may not be immediately available in the DOM, such as when the pagination is rendered conditionally or updated dynamically. Additionally,
// it employs a MutationObserver to monitor changes in the DOM and reapply the necessary accessibility attributes as needed, ensuring consistent
// accessibility improvements even if the pagination's content changes over time.
// Usage: Add the paginationAccessibility directive to any goa-pagination element and provide a customAriaLabel input to enhance its accessibility
// by ensuring that the internal input and icon elements have meaningful aria-labels for screen readers.
@Directive({
  selector: '[paginationAccessibility]',
  standalone: true,
})
export class PaginationAccessibilityDirective
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() customAriaLabel = '';

  private observer?: MutationObserver;
  private retryTimer?: ReturnType<typeof setTimeout>;
  private readonly maxAttempts = 20;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) {}

  ngAfterViewInit(): void {
    this.applyAriaLabelWithRetry();
    this.observeDomChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customAriaLabel']) {
      this.applyAriaLabelWithRetry();
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
    }
  }

  private observeDomChanges(): void {
    this.observer = new MutationObserver(() => this.applyAriaLabelWithRetry());
    this.observer.observe(this.el.nativeElement, {
      childList: true,
      subtree: true,
    });
  }

  private applyAriaLabelWithRetry(attempt = 0): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
    }

    const updated = this.applyAriaLabel();
    if (updated || attempt >= this.maxAttempts) {
      return;
    }

    this.retryTimer = setTimeout(() => {
      this.applyAriaLabelWithRetry(attempt + 1);
    }, 100);
  }

  private applyAriaLabel(): boolean {
    const label = this.customAriaLabel?.trim();
    if (!label) {
      return false;
    }

    const inputElements = this.findComboboxInputs(this.el.nativeElement);
    inputElements.forEach((inputElement) => {
      this.renderer.removeAttribute(inputElement, 'aria-labelledby');
      this.renderer.setAttribute(inputElement, 'aria-label', label);
    });

    const iconElements = this.findIconElements(this.el.nativeElement);
    iconElements.forEach((iconElement) => {
      this.renderer.setAttribute(iconElement, 'aria-label', label);
    });

    return inputElements.length > 0 || iconElements.length > 0;
  }

  private findComboboxInputs(root: ParentNode): HTMLElement[] {
    const results: HTMLElement[] = [];
    const stack: ParentNode[] = [root];

    while (stack.length) {
      const current = stack.pop();
      if (!current) {
        continue;
      }

      const found = current.querySelectorAll('input[role="combobox"]');
      found.forEach((input) => {
        if (input instanceof HTMLElement) {
          results.push(input);
        }
      });

      const elements = current.querySelectorAll('*');
      elements.forEach((element) => {
        if (element.shadowRoot) {
          stack.push(element.shadowRoot);
        }
      });
    }

    return results;
  }

  private findIconElements(root: ParentNode): HTMLElement[] {
    const results: HTMLElement[] = [];
    const stack: ParentNode[] = [root];

    while (stack.length) {
      const current = stack.pop();
      if (!current) {
        continue;
      }

      const found = current.querySelectorAll('goa-icon');
      found.forEach((icon) => {
        const iconDiv =
          icon.shadowRoot?.querySelector(
            'div[role="img"][data-type="chevron-down"]'
          ) ??
          icon.shadowRoot?.querySelector('div[role="img"]') ??
          icon.querySelector('div[role="img"]');
        if (iconDiv instanceof HTMLElement) {
          results.push(iconDiv);
        }
      });

      const elements = current.querySelectorAll('*');
      elements.forEach((element) => {
        if (element.shadowRoot) {
          stack.push(element.shadowRoot);
        }
      });
    }

    return results;
  }
}
